import { Button, Dialog } from '@mui/material';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import RouterWrapper from '@/utils/RouterWrapper';
import SearchStore from '@/stores/SearchStore';
import Currencies from '@/utils/Currencies';
import { RESERVATION_DIALOG_VARIANT } from '../helpers/index';
import ReservationActions from '@/actions/ReservationActions';
import HotelActions from '@/actions/HotelActions';
import { withRouter } from '@/utils/withRouter';
import companyIds from 'arbitrip-common/theme/mapping';
import { COMPANY_NAME } from 'arbitrip-common/theme/constants';
import { isElalCompany } from '@/utils/isCompany';

class ReservationDialog extends React.Component {
    getTitleText() {
        const { variant, profile, t } = this.props;
        const tripManagerName = (profile && profile.approver_full_name) || t('ReservationDialog.DefaultTripManager');

        switch (variant) {
            case RESERVATION_DIALOG_VARIANT.SUCCESS:
                return t('ReservationDialog.SuccessTitle');
            case RESERVATION_DIALOG_VARIANT.FAILED:
                return t('ReservationDialog.FailedTitle');
            case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
            case RESERVATION_DIALOG_VARIANT.LAZY_RECHECK_NO_AVAILABILITY:
                return t('ReservationDialog.RoomNotAvailableTitle');
            case RESERVATION_DIALOG_VARIANT.PENDING:
                return t('ReservationDialog.PendingTitle');
            case RESERVATION_DIALOG_VARIANT.QUOTE:
                return t('ReservationDialog.QuoteTitle');
            case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
                return (
                    <Trans
                        i18nKey="ReservationDialog.OutOfPolicyTitle"
                        values={{ tripManagerName }}
                        components={[<b />]}
                    />
                );
			case RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR:
				return t('ReservationDialog.LoyaltyCardErrorTitle');

            default:
                return null;
        }
    }

    getMainMessageComponent() {
        const { variant, shortId, tripName, t } = this.props;

        switch (variant) {
            case RESERVATION_DIALOG_VARIANT.SUCCESS:
                return (
                    <p className="message">
                        <Trans
                            i18nKey="ReservationDialog.SuccessMessage"
                            values={{ shortId }}
                            components={[<b />, <br />]}
                        />
                    </p>
                );
            case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
                return <p className="message">{t('ReservationDialog.OutOfPolicyMessage')}</p>;
            case RESERVATION_DIALOG_VARIANT.QUOTE:
                return (
                    <p className="message">
                        <Trans i18nKey="ReservationDialog.QuoteMessage" values={{ tripName }} components={[<b />]} />
                    </p>
                );
            case RESERVATION_DIALOG_VARIANT.FAILED:
                return (
                    <p className="message">
                        <Trans i18nKey="ReservationDialog.FailedMessage" components={[<br />]} />
                    </p>
                );
            case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
            case RESERVATION_DIALOG_VARIANT.LAZY_RECHECK_NO_AVAILABILITY:
                return (
                    <p className="message">
                        <Trans i18nKey="ReservationDialog.RoomNotAvailableMessage" />
                    </p>
                );
            case RESERVATION_DIALOG_VARIANT.PENDING:
                return (
                    <p className="message">
                        <Trans i18nKey="ReservationDialog.PendingMessage" components={[<br />]} />
                    </p>
                );
            case RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR:
				return (
					<p className="message">
						{t('PaymentIsEnabledOnlyWithBanHapoalim')}
					</p>
				);
			default:
				return null;
		}
	}

    getAdditionalMessageComponent() {
        const { variant, isNonRefundable, t } = this.props;

        switch (variant) {
            case RESERVATION_DIALOG_VARIANT.SUCCESS:
            case RESERVATION_DIALOG_VARIANT.FAILED:
            case RESERVATION_DIALOG_VARIANT.QUOTE:
            case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
            case RESERVATION_DIALOG_VARIANT.LAZY_RECHECK_NO_AVAILABILITY:
            case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
			case RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR:
				return <div className="additional-placeholder" />;
			case RESERVATION_DIALOG_VARIANT.PENDING:
				return isNonRefundable ? (
					<div className="additional-message">
						<div className="additional-message-text">
							<Trans
								i18nKey="ReservationDialog.PendingAdditionalMessage"
								values={{ nonRefundable: t('ReservationDialog.NonRefundable') }}
								components={[<b />, <br />]}
							/>
						</div>
					</div>
				) : (
					<div className="additional-placeholder" />

                );
            default:
                return null;
        }
    }

    getActionButtonsComponent() {
        const { variant, primeButtonAction, t } = this.props;

        switch (variant) {
            case RESERVATION_DIALOG_VARIANT.SUCCESS:
            case RESERVATION_DIALOG_VARIANT.PENDING:
            case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
                return (
                    <Button
                        className="button-primary"
                        key={'close-button'}
                        variant="text"
                        color="primary"
                        onClick={primeButtonAction}
                        data-testid="close-reservation-dialog"
                    >
                        {t('ReservationDialog.CloseButton')}
                    </Button>
                );
            case RESERVATION_DIALOG_VARIANT.QUOTE:
                return (
                    <Button
                        className="button-primary"
                        key={'close-button'}
                        variant="text"
                        color="primary"
                        onClick={primeButtonAction}
                    >
                        {t('ReservationDialog.ViewTripButton')}
                    </Button>
                );
            case RESERVATION_DIALOG_VARIANT.FAILED:
            case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
                return (
                    <Button
                        className="button-primary"
                        key={'close-button'}
                        variant="text"
                        color="primary"
                        onClick={primeButtonAction}
                        data-testid="reservation-dialog-select-another-room"
                    >
                        {t('ReservationDialog.SelectAnotherRoomButton')}
                    </Button>
                );
            case RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR:
				return (
					<Button
						className="button-primary"
						key={'close-button'}
						variant="text"
						color="primary"
						onClick={primeButtonAction}
					>
						{t('ReservationDialog.RetryButton')}
					</Button>
				);
			case RESERVATION_DIALOG_VARIANT.LAZY_RECHECK_NO_AVAILABILITY:
                return (
                    <div className="reservation-dialog-actions">
                        <Button
                            className="button prime"
                            onClick={this.props.onBack}
                            variant="contained"
                            color="primary"
                            data-testid="reservation-dialog-select-another-room"
                        >
                            {t('ReservationDialog.SelectAnotherRoomButton')}
                        </Button>
                    </div>
                );
            default:
                return null;
        }
    }

    getLongIdComponent() {
        const { variant, reservationId } = this.props;

        if (!reservationId) {
            return null;
        }

        switch (variant) {
            case RESERVATION_DIALOG_VARIANT.PENDING:
            case RESERVATION_DIALOG_VARIANT.FAILED:
            case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
                return <div className="id-text">ID: {reservationId}</div>;
            case RESERVATION_DIALOG_VARIANT.SUCCESS:
            case RESERVATION_DIALOG_VARIANT.QUOTE:
            case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
            case RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR:
			default:
				return null;
		}
	}

    getFooter() {
        const { variant, profile, t } = this.props;
        const isLetStay = companyIds[profile.company.id] === COMPANY_NAME.LS;

		if (isLetStay) {
			return null;
		}

        switch (variant) {
            case RESERVATION_DIALOG_VARIANT.PENDING:
            case RESERVATION_DIALOG_VARIANT.FAILED:
                return (
                    <div className="footer">
                        <div className="footer-header">{t('ReservationDialog.FooterHeader')}</div>
                        <div className="footer-numbers flex">
                            <div>{t('ReservationDialog.FooterUSNumber')}</div>
                            <div>|</div>
                            <div>{t('ReservationDialog.FooterUKNumber')}</div>
                            <div>|</div>
                            <div>{t('ReservationDialog.FooterILNumber')}</div>
                        </div>
                    </div>
                );
            case RESERVATION_DIALOG_VARIANT.QUOTE:
            case RESERVATION_DIALOG_VARIANT.SUCCESS:
            case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
            case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
            case RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR:
			default:
				return null;
		}
	}

    render() {
        const { variant } = this.props;
        let titleClasses = 'title';
        let imageClasses = 'image_header';
        switch (variant) {
            case RESERVATION_DIALOG_VARIANT.PENDING:
                titleClasses += ' green';
                imageClasses += ' pending-icon';
                break;
            case RESERVATION_DIALOG_VARIANT.SUCCESS:
            case RESERVATION_DIALOG_VARIANT.QUOTE:
            case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
                titleClasses += ' green';
                imageClasses += ' confirm-icon';
                break;
            case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
            case RESERVATION_DIALOG_VARIANT.LAZY_RECHECK_NO_AVAILABILITY:
            case RESERVATION_DIALOG_VARIANT.FAILED:
            case RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR:
                titleClasses += ' red';
                imageClasses += ' error-icon';
                break;
            default:
                break;
        }
        return (
            <Dialog
                className={'reservation-dialog-main'}
                open={this.props.modalIsOpen}
                aria-describedby="alert-dialog-description"
                fullWidth={true}
            >
                <div className="reservation-dialog-container" data-testid="reservation-dialog">
                    <div className={imageClasses} />
                    <h3 className={titleClasses}>{this.getTitleText()}</h3>
                    {this.getMainMessageComponent()}
                    {this.getAdditionalMessageComponent()}
                    {this.getActionButtonsComponent()}
                    {this.getLongIdComponent()}
                    {this.getFooter()}
                </div>
            </Dialog>
        );
    }
}

ReservationDialog.propTypes = {
    shortId: PropTypes.string,
    reservationId: PropTypes.string,
    variant: PropTypes.oneOf([
        RESERVATION_DIALOG_VARIANT.FAILED,
        RESERVATION_DIALOG_VARIANT.SUCCESS,
        RESERVATION_DIALOG_VARIANT.PENDING,
        RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE,
        RESERVATION_DIALOG_VARIANT.QUOTE,
        RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY,
        RESERVATION_DIALOG_VARIANT.LAZY_RECHECK_NO_AVAILABILITY,
        RESERVATION_DIALOG_VARIANT.LOYALTY_CARD_ERROR,
    ]).isRequired,
    profile: PropTypes.object,
    primeButtonAction: PropTypes.func,
    secondaryButtonAction: PropTypes.func,
    modalIsOpen: PropTypes.bool.isRequired,
    tripName: PropTypes.string,
    isNonRefundable: PropTypes.bool,
    t: PropTypes.func.isRequired,
};

export default memo(withTranslation()(ReservationDialog));
